import type { PosPriceMap, PosProduct, PosVariant } from './api';

/**
 * Shared POS price derivation, used by the product grid, the cart, the Review
 * modal and the variant picker. These live outside `pages/pos.tsx` so the grid
 * can be its own component without duplicating pricing logic — the numbers on
 * a tile and the numbers on the matching cart line must always agree.
 */

/**
 * Live cash price of a product/variant = the selected slot's discounted price
 * (from the price-map overlay) when present, else the branch base price. `base`
 * is the SRP shown struck-through when a discount applies. Deriving price at
 * render (not storing it on the cart line) is what makes switching slots
 * re-price the grid AND already-carted lines instantly, with no stale price.
 */
export function productPrice(
  p: PosProduct,
  priceMap: PosPriceMap | null,
): { now: number; base: number } {
  return {
    now: Number(priceMap?.products[p.id]?.unitPrice ?? p.effectivePrice),
    base: Number(p.basePrice),
  };
}

export function variantPrice(
  v: PosVariant,
  priceMap: PosPriceMap | null,
): { now: number; base: number } {
  return { now: Number(priceMap?.variants[v.id]?.unitPrice ?? v.price), base: Number(v.price) };
}

/** A product+variant pair, i.e. the shape of a cart line for pricing purposes. */
export type PricedLine = { product: PosProduct; variant: PosVariant | null };

/** Unit price for a cart line, slot-discounted when the price map has it. */
export function lineUnitPrice(l: PricedLine, priceMap: PosPriceMap | null): number {
  return l.variant ? variantPrice(l.variant, priceMap).now : productPrice(l.product, priceMap).now;
}

/** Stock cap for a line: the chosen variant's branch stock, else the product's. */
export function lineStockCap(l: PricedLine): number {
  return l.variant ? l.variant.stockQty : l.product.sellableQty;
}

/**
 * A price that shows the SRP struck-through + the discounted price + a `−X%`
 * tag whenever `now < base` (a slot discount is live); otherwise just the price.
 * `−X%` uses `text-success` to match the Review modal's per-line discount.
 */
export function PriceTag({
  base,
  now,
  prefix = '',
}: {
  base: number;
  now: number;
  prefix?: string;
}) {
  if (!(now < base))
    return (
      <span className="font-semibold">
        {prefix}₱{now.toFixed(2)}
      </span>
    );
  const pct = Math.round((1 - now / base) * 100);
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="text-xs text-muted-foreground line-through">
        {prefix}₱{base.toFixed(2)}
      </span>
      <span className="font-semibold text-success">
        {prefix}₱{now.toFixed(2)}
      </span>
      {pct > 0 && <span className="text-[10px] text-success">−{pct}%</span>}
    </span>
  );
}
